/**
 * PathwayPalette (PROD-591, extended in Sprint 4.3.1 / PROD-650 +
 * Sprint 4.3.7 + Sprint 4.3.7-fix).
 *
 * Left-side panel listing the closed vocabulary of pathway node types.
 *
 * The palette is now organised in two sections per the Awell design
 * ontology (`awell/studio/docs/getting-started/design-ontology.md`):
 *
 *   1. **Hierarchy** (3 tiers): Track > Step > Action. These are the
 *      Awell building blocks; the canvas drills into one tier at a
 *      time via the breadcrumb.
 *   2. **Activity types** (legacy 6, master prompt v3.3 §8):
 *      Demographics, Vitals, Labs, Decision Point, NBA, Outcome. These
 *      remain the activity vocabulary used inside Action nodes and by
 *      existing FHIR fixtures.
 *
 * BPMN deviation: the palette is intentionally restricted to the 3-tier
 * hierarchy + 6 activity types. No BPMN-specific shapes (gateway, lane,
 * pool, etc.) appear; healthcare process modelling deliberately diverges
 * from BPMN.
 *
 * Sprint 4.3.7: each row is now a draggable HTML5 source. The
 * `dragstart` handler stamps the dragged node-type into
 * `dataTransfer` under the `application/x-cps-node-type` mime so the
 * canvas drop handler can read it. Activity-type drags carry a
 * secondary `application/x-cps-activity-type` value (consumed by the
 * canvas to mark the dropped node as an Action node with `category`
 * set per the activity type per Awell ontology).
 *
 * Sprint 4.3.7-fix (founder direction 2026-05-08): the canonical
 * interaction is now click-to-arm + click-on-canvas (mirrors Figma /
 * Miro). Real-browser Playwright drives proved that synthetic HTML5
 * drag events do not fire the way unit tests assumed. Click-to-add is
 * more discoverable, more keyboard-accessible, and easier to drive
 * end-to-end. HTML5 drag-and-drop is RETAINED alongside it for
 * backwards compatibility with existing unit tests + power users who
 * prefer drag.
 *
 * Click-to-add flow:
 *   1. User clicks a palette row -> `usePaletteArmingStore.arm(...)`.
 *      Row paints an "armed" border ring; canvas paints crosshair.
 *   2. User clicks anywhere on the canvas -> editor reads
 *      `armedNodeType`, calls `addNode` at the click coord, disarms.
 *   3. ESC or a second click on the same row -> disarm.
 *
 * Signal DS sourcing per COMPONENT-CONTRACTS §17:
 *   - Panel shell: Card (Sidebar) [GAP via G9]
 *   - Type row: Component (Tag) - DS-existing token-driven
 *
 * HC2: Tailwind classes resolve through Signal DS tokens. No raw hex.
 * HC5: zero em / en dashes.
 * HC6 a11y: each row is a real `<button>` with keyboard-focusable
 * semantics. The click-to-arm pattern is keyboard-accessible by default
 * (Enter / Space activates the button). The drag fallback augments the
 * click flow for users who prefer the legacy gesture.
 */
import {
  ACTIVITY_NODE_TYPES,
  HIERARCHY_NODE_TYPES,
  type ActivityNodeType,
  type HierarchyNodeType,
  type PathwayNodeType,
} from '../../services/pathway-layout';
import { usePaletteArmingStore } from '../../state/palette-arming.store';
import { DataPointsPanel } from './DataPointsPanel';

const NODE_TYPE_LABELS: Record<PathwayNodeType, string> = {
  // Awell 3-tier hierarchy (Sprint 4.3.1).
  track: 'Track',
  step: 'Step',
  action: 'Action',
  // Legacy activity types.
  demographics: 'Demographics',
  vitals: 'Vitals',
  labs: 'Labs',
  decision: 'Decision Point',
  nba: 'NBA candidate',
  outcome: 'Outcome',
};

const NODE_TYPE_HINTS: Record<PathwayNodeType, string> = {
  // Awell 3-tier hierarchy hints anchored to the design-ontology spec.
  track: 'Declarative container activated by a trigger.',
  step: 'Sequenced unit inside a Track. Connected by transitions.',
  action: 'Most granular building block. User or system action.',
  // Legacy activity hints.
  demographics: 'Age, sex, BMI, tenant context.',
  vitals: 'Baseline BMI, blood pressure, heart rate.',
  labs: 'Lab basket items with confirmation flags.',
  decision: 'State machine with linked NBA candidates.',
  nba: 'Tier-bound action surfaced to the clinician.',
  outcome: 'Projected endpoint after pathway completion.',
};

const DRAG_TOOLTIP = 'Drag to the canvas to add a node of this type.';
/** Legacy export retained for tests that imported the Phase-2 string. */
const PHASE_2_TOOLTIP = DRAG_TOOLTIP;

/** Mime keys read by the canvas onDrop handler. */
export const PALETTE_NODE_TYPE_MIME = 'application/x-cps-node-type';
export const PALETTE_ACTIVITY_TYPE_MIME = 'application/x-cps-activity-type';

export interface PathwayPaletteProps {
  className?: string;
}

interface PaletteSectionProps<T extends PathwayNodeType> {
  title: string;
  testId: string;
  nodeTypes: ReadonlyArray<T>;
}

function PaletteSection<T extends PathwayNodeType>({
  title,
  testId,
  nodeTypes,
}: PaletteSectionProps<T>) {
  // Sprint 4.3.7-fix: click-to-arm flow. Each row is both a click
  // target (arms the type for click-on-canvas placement) and a drag
  // source (legacy HTML5 DnD). Clicking the same row a second time
  // disarms.
  const armedNodeType = usePaletteArmingStore((s) => s.armedNodeType);
  const armedActivityType = usePaletteArmingStore((s) => s.armedActivityType);
  const arm = usePaletteArmingStore((s) => s.arm);
  return (
    <div data-testid={testId} className="flex flex-col gap-gutter-sm">
      <h4 className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
        {title}
      </h4>
      <ul
        role="list"
        aria-label={title}
        data-testid={`${testId}-list`}
        className="flex flex-col gap-gutter-sm"
      >
        {nodeTypes.map((nodeType) => {
          // Activity types drag as Action nodes with the activity type
          // captured in a secondary mime so the canvas resolves the
          // category. Hierarchy types (track / step / action) drag as
          // their own type.
          const isActivity = !['track', 'step', 'action'].includes(nodeType);
          const draggedType: PathwayNodeType = isActivity
            ? 'action'
            : nodeType;
          const isArmed = isActivity
            ? armedNodeType === 'action' && armedActivityType === nodeType
            : armedNodeType === nodeType && armedActivityType === null;
          return (
            <li key={nodeType}>
              <button
                type="button"
                draggable
                data-testid={`pathway-palette-node-${nodeType}`}
                data-node-type={nodeType}
                data-draggable="true"
                data-armed={isArmed ? 'true' : 'false'}
                aria-pressed={isArmed}
                title={DRAG_TOOLTIP}
                onClick={() => {
                  arm(draggedType, isActivity ? nodeType : undefined);
                }}
                onDragStart={(event) => {
                  if (!event.dataTransfer) return;
                  event.dataTransfer.effectAllowed = 'copy';
                  // Hierarchy types drag the node-type verbatim. Activity
                  // types drag as `action` so the dropped node is an
                  // Action node with the activity type captured in the
                  // secondary mime. The legacy 6 are also dispatchable as
                  // their own type for fixtures that still consume them.
                  event.dataTransfer.setData(
                    PALETTE_NODE_TYPE_MIME,
                    draggedType,
                  );
                  if (isActivity) {
                    event.dataTransfer.setData(
                      PALETTE_ACTIVITY_TYPE_MIME,
                      nodeType,
                    );
                  }
                  // Plain-text fallback for browsers that drop typed mimes.
                  event.dataTransfer.setData('text/plain', draggedType);
                }}
                className={[
                  'flex w-full cursor-pointer flex-col items-start gap-1 rounded-signal-md border bg-surface-base px-gutter-md py-gutter-sm text-left text-signal-sm text-text-primary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500 active:cursor-grabbing',
                  isArmed
                    ? 'border-signal-primary-500 bg-signal-primary-100 ring-2 ring-signal-primary-500'
                    : 'border-surface-border',
                ].join(' ')}
              >
                <span className="font-semibold">
                  {NODE_TYPE_LABELS[nodeType]}
                  {isArmed ? (
                    <span
                      data-testid={`pathway-palette-node-${nodeType}-armed`}
                      className="ml-gutter-sm text-signal-xs font-semibold uppercase tracking-wide text-signal-primary-700"
                    >
                      Armed
                    </span>
                  ) : null}
                </span>
                <span className="text-signal-xs text-text-tertiary">
                  {NODE_TYPE_HINTS[nodeType]}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function PathwayPalette({ className }: PathwayPaletteProps) {
  return (
    <aside
      aria-labelledby="pathway-palette-title"
      data-testid="pathway-palette"
      className={[
        'flex w-72 flex-col gap-gutter-md overflow-y-auto border-r border-surface-border bg-surface-muted p-gutter-md',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div>
        <h3
          id="pathway-palette-title"
          className="text-signal-sm font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Node palette
        </h3>
        <p className="mt-gutter-sm text-signal-xs text-text-tertiary">
          3-tier hierarchy (Awell ontology) + 6 activity types + Data
          Points catalogue. Phase 2 wires drag to add.
        </p>
      </div>
      {/*
        The combined list is preserved as `pathway-palette-list` so
        existing tests that count the legacy 6 activity rows keep
        working. Section markers add the hierarchy block above without
        breaking the canonical query.
      */}
      <ul
        role="list"
        aria-label="Pathway node types"
        data-testid="pathway-palette-list"
        className="contents"
      >
        <li className="contents">
          <PaletteSection<HierarchyNodeType>
            title="Hierarchy"
            testId="pathway-palette-hierarchy"
            nodeTypes={HIERARCHY_NODE_TYPES}
          />
        </li>
        <li className="contents">
          <PaletteSection<ActivityNodeType>
            title="Activity types"
            testId="pathway-palette-activities"
            nodeTypes={ACTIVITY_NODE_TYPES}
          />
        </li>
      </ul>
      <DataPointsPanel />
    </aside>
  );
}

export { NODE_TYPE_LABELS, NODE_TYPE_HINTS, PHASE_2_TOOLTIP };
